/**
 * The case at a glance: the playbook and the report type, the stage, when
 * it's due, the price, and what it has cost so far — model spend against
 * the threshold the case runs under, and NP fees. The Assistant's toggle
 * and the hand-back sit here too.
 */

import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Anchor, Badge, Button, Card, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';

import { dueInfo } from '@/components/Inbox/queueFormat';
import { timeAgo } from '@/components/Inbox/timeAgo';
import { casePathOf, type WorkbenchHeader as Header } from '@/types/workbench';
import { formatMoney } from '@/utils';
import { formatUsd, words } from './format';
import { HandBackButton } from './HandBackButton';
import { RemoveCopyButton } from './RemoveCopyButton';

function Fact({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" component="div">
        {children}
      </Text>
    </Stack>
  );
}

function Due({ header }: Readonly<{ header: Header }>) {
  const due = dueInfo({ dueAt: header.due.dueAt, turnaroundDays: header.due.turnaroundDays });
  if (!due) {
    return <>{header.startedAt ? '—' : 'Not started'}</>;
  }
  return (
    <Tooltip label={`Due ${due.exact}${header.due.overridden ? ' (fixed date)' : ''}`} withArrow>
      <Badge
        size="sm"
        variant="light"
        color={due.tone === 'overdue' ? 'red' : due.tone === 'warning' ? 'orange' : 'gray'}
        style={{ textTransform: 'none' }}
        data-testid="workbench-due"
      >
        {due.tone === 'overdue' ? due.label : `in ${due.label}`}
      </Badge>
    </Tooltip>
  );
}

/** Model spend against the threshold, and NP fees. */
function Spend({ header }: Readonly<{ header: Header }>) {
  const { modelsUsd, npFees, threshold } = header.spend;
  const parts: string[] = [];
  const models = formatUsd(modelsUsd);
  if (models && Number(modelsUsd) > 0) {
    parts.push(`Agents & providers ${models}`);
  }
  for (const fee of npFees) {
    const money = formatMoney(fee.amount, fee.currency);
    if (money) {
      parts.push(`NP fees ${money}${fee.currency ? '' : ' (currency not recorded)'}`);
    }
  }
  const cap = threshold.usd === null ? null : formatUsd(threshold.usd);
  return (
    <Group gap={6} data-testid="workbench-spend">
      <Text size="sm" component="span">
        {parts.length > 0 ? parts.join(' · ') : 'Nothing yet'}
      </Text>
      {cap ? (
        <Tooltip
          label={`${formatUsd(threshold.spent)} of ${cap} — ${
            threshold.scope === 'before_start' ? 'the budget before Start' : 'the case budget'
          }; a threshold you raise, never a wall`}
          withArrow
        >
          <Badge
            size="sm"
            variant={threshold.reached ? 'filled' : 'outline'}
            color={threshold.reached ? 'orange' : 'gray'}
            style={{ textTransform: 'none' }}
            data-testid="workbench-threshold"
          >
            {threshold.reached ? 'At the threshold' : `of ${cap}`}
          </Badge>
        </Tooltip>
      ) : null}
    </Group>
  );
}

/** The case agent: at work on the case now, or idle since its last statement. */
function Run({ header }: Readonly<{ header: Header }>) {
  const { run } = header;
  if (!run.status) {
    return (
      <Text size="sm" c="dimmed" component="span" data-testid="workbench-run">
        {run.lastStatementAt ? `Idle · last statement ${timeAgo(run.lastStatementAt)}` : 'Idle'}
      </Text>
    );
  }
  return (
    <Group gap={6} wrap="nowrap" data-testid="workbench-run">
      <Loader size={14} color="blue" />
      <Text size="sm" component="span">
        {run.status === 'queued' ? 'Queued' : 'Working'}
        {run.reason ? ` · ${words(run.reason)}` : ''}
      </Text>
    </Group>
  );
}

interface WorkbenchHeaderProps {
  header: Header;
  /** After a hand-back: the case has left the workbench. */
  onHandedBack: () => void | Promise<void>;
  /** The Assistant beside the workbench: open, and its toggle. */
  copilotOpen?: boolean;
  onToggleCopilot?: () => void;
  /** An evaluation copy was removed: the page leaves it. */
  onRemoved?: () => void | Promise<void>;
}

export const WorkbenchHeader = memo(
  ({
    header,
    onHandedBack,
    onRemoved,
    copilotOpen = false,
    onToggleCopilot,
  }: Readonly<WorkbenchHeaderProps>) => {
    const price = header.price ? formatMoney(header.price.amount, header.price.currency) : null;
    const { playbook, evaluationOf } = header;
    const sourcePath = evaluationOf
      ? casePathOf(evaluationOf.familyKey, evaluationOf.reportId)
      : null;
    return (
      <Card withBorder padding="sm" radius="md" data-testid="workbench-header">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Group gap="xl" wrap="wrap" align="flex-start">
            <Fact label="Playbook">
              {playbook ? (
                <Anchor
                  component={Link}
                  to={`/admin/playbooks/${playbook.key}`}
                  size="sm"
                  data-testid="workbench-playbook-link"
                >
                  {playbook.name} · v{playbook.version}
                </Anchor>
              ) : (
                '—'
              )}
            </Fact>
            <Fact label="Report type">
              {playbook?.reportType ? (
                <Badge
                  size="sm"
                  variant="light"
                  color="blue"
                  style={{ textTransform: 'none' }}
                  data-testid="workbench-report-type"
                >
                  {playbook.reportType}
                </Badge>
              ) : (
                <Text size="sm" c="dimmed" component="span" data-testid="workbench-report-type">
                  Not chosen
                </Text>
              )}
            </Fact>
            <Fact label="Stage">{words(header.lifecycleStage)}</Fact>
            {evaluationOf ? (
              <Fact label="Evaluation copy of">
                {sourcePath ? (
                  <Anchor
                    component={Link}
                    to={sourcePath}
                    size="sm"
                    data-testid="workbench-copy-of"
                  >
                    {evaluationOf.codename ?? 'the case'}
                  </Anchor>
                ) : (
                  <Text size="sm" component="span" data-testid="workbench-copy-of">
                    {evaluationOf.codename ?? 'the case'}
                  </Text>
                )}
              </Fact>
            ) : null}
            <Fact label="Due">
              <Group gap={6}>
                <Due header={header} />
                {header.due.expedited && (
                  <Badge size="sm" variant="light" color="orange">
                    Expedited
                  </Badge>
                )}
              </Group>
            </Fact>
            <Fact label="Price">{price ?? '—'}</Fact>
            <Fact label="Spent so far">
              <Spend header={header} />
            </Fact>
            <Fact label="Case agent">
              <Run header={header} />
            </Fact>
          </Group>
          <Group gap="xs">
            {onToggleCopilot ? (
              <Button
                size="xs"
                variant={copilotOpen ? 'light' : 'default'}
                color="violet"
                leftSection={<IconSparkles size={14} />}
                onClick={onToggleCopilot}
                aria-pressed={copilotOpen}
                data-testid="workbench-copilot-toggle"
              >
                Assistant
              </Button>
            ) : null}
            {evaluationOf ? (
              <RemoveCopyButton reportId={header.reportId} onDone={onRemoved ?? onHandedBack} />
            ) : (
              <HandBackButton reportId={header.reportId} onDone={onHandedBack} />
            )}
          </Group>
        </Group>
      </Card>
    );
  }
);
